import { useState, useCallback, useEffect } from 'react';
import type { Deck, AppSettings, StudySession, AppState, StudySettings, Flashcard } from '../types';
import { storage } from '../services/storage';

export function useAppState() {
  const [state, setState] = useState<AppState>(() => {
    const settings = storage.getSettings();
    const decks = storage.getDecks();
    const sessions = storage.getSessions();
    return { decks, sessions, settings, generationHistory: [] };
  });

  const loadState = useCallback(() => {
    const decks = storage.getDecks();
    const sessions = storage.getSessions();
    const settings = storage.getSettings();
    setState({ decks, sessions, settings, generationHistory: [] });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      loadState();
    }
  }, [loadState]);

  // Deck operations
  const addDeck = useCallback((deck: Omit<Deck, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newDeck = storage.createDeck(deck);
    setState((prev) => ({
      ...prev,
      decks: [...prev.decks, newDeck],
    }));
    return newDeck;
  }, []);

  const updateDeck = useCallback((id: string, updates: Partial<Deck>) => {
    const updated = storage.updateDeck(id, updates);
    if (updated) {
      setState((prev) => ({
        ...prev,
        decks: prev.decks.map((deck) => (deck.id === id ? updated : deck)),
      }));
    }
    return updated;
  }, []);

  const deleteDeck = useCallback((id: string) => {
    const success = storage.deleteDeck(id);
    if (success) {
      setState((prev) => ({
        ...prev,
        decks: prev.decks.filter((deck) => deck.id !== id),
      }));
    }
    return success;
  }, []);

  const addCardToDeck = useCallback((deckId: string, card: Omit<Flashcard, 'id' | 'createdAt'>) => {
    setState((prev) => {
      const decks = prev.decks.map((deck) => {
        if (deck.id !== deckId) return deck;
        const newCard: Flashcard = {
          ...card,
          id: `card-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          createdAt: new Date().toISOString(),
        };
        return { ...deck, cards: [...deck.cards, newCard], updatedAt: new Date().toISOString() };
      });
      storage.saveDecks(decks);
      return { ...prev, decks };
    });
  }, []);

  const removeCardFromDeck = useCallback((deckId: string, cardId: string) => {
    setState((prev) => {
      const decks = prev.decks.map((deck) => {
        if (deck.id !== deckId) return deck;
        return { ...deck, cards: deck.cards.filter((card) => card.id !== cardId) };
      });
      storage.saveDecks(decks);
      return { ...prev, decks };
    });
  }, []);

  const updateCard = useCallback((deckId: string, cardId: string, updates: Partial<Flashcard>) => {
    setState((prev) => {
      const decks = prev.decks.map((deck) => {
        if (deck.id !== deckId) return deck;
        return {
          ...deck,
          cards: deck.cards.map((card) => (card.id === cardId ? { ...card, ...updates } : card)),
        };
      });
      storage.saveDecks(decks);
      return { ...prev, decks };
    });
  }, []);

  // Settings operations
  const updateSettings = useCallback((updates: Partial<AppSettings>) => {
    const currentSettings = storage.getSettings();
    const newSettings = { ...currentSettings, ...updates };
    storage.saveSettings(newSettings);
    setState((prev) => ({ ...prev, settings: newSettings }));
  }, []);

  const updateStudySettings = useCallback((settings: Partial<StudySettings>) => {
    const currentSettings = storage.getSettings();
    const newSettings = {
      ...currentSettings,
      defaultStudySettings: { ...currentSettings.defaultStudySettings, ...settings },
    };
    storage.saveSettings(newSettings);
    setState((prev) => ({ ...prev, settings: newSettings }));
  }, []);

  // Session operations
  const addSession = useCallback((session: StudySession) => {
    storage.addSession(session);
    setState((prev) => ({
      ...prev,
      sessions: [...prev.sessions, session],
    }));
  }, []);

  const getSessionStats = useCallback(() => {
    const sessions = storage.getSessions();
    const totalCards = sessions.reduce((acc, session) => acc + session.results.length, 0);
    const averageQuality =
      sessions.length > 0
        ? sessions.reduce((acc, session) => acc + session.results.reduce((a, r) => a + r.quality, 0), 0) /
          totalCards || 0
        : 0;
    return { totalSessions: sessions.length, totalCards, averageQuality };
  }, []);

  const importData = useCallback((json: string) => {
    const result = storage.importData(json);
    if (result.success) {
      loadState();
    }
    return result;
  }, [loadState]);

  const exportData = useCallback(() => {
    return storage.exportData();
  }, []);

  const clearAllData = useCallback(() => {
    storage.clearAllData();
    setState({ decks: [], sessions: [], settings: storage.getDefaultSettings(), generationHistory: [] });
  }, []);

  return {
    ...state,
    loadState,
    addDeck,
    updateDeck,
    deleteDeck,
    addCardToDeck,
    removeCardFromDeck,
    updateCard,
    updateSettings,
    updateStudySettings,
    addSession,
    getSessionStats,
    importData,
    exportData,
    clearAllData,
  };
}

export default useAppState;
